import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  Tag,
  Divider,
  Table,
  Statistic,
  Card,
  Row,
  Col,
  Spin,
  Typography,
  Space,
} from "antd";
import { useAuth0 } from "../../../../react-auth0-spa";
import format from "date-fns/format";
import { PlusOutlined } from "@ant-design/icons";
import CreditiModal from "./CreditiModal";
import config from "../../../../api_config.json";
import { useAppContext } from "../../../../context";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
} from "chart.js";
import { Chart } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController
);

const { Title: AntTitle } = Typography;

const OrgCrediti = (props) => {
  const [creditiModalVisible, setCreditiModalVisible] = useState(false);
  const [creditiModalResult, setCreditiModalResult] = useState();
  const { orgId } = props;

  // State for credits and orders
  const [credits, setCredits] = useState({
    freeCreditsAmount: 0,
    paidCreditsAmount: 0,
  });
  const [creditOrders, setCreditOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]); // Store all orders for charts
  const [periodStats, setPeriodStats] = useState({
    creditsUsed: 0,
    creditsAddon: 0,
    creditsSubscription: 0,
  });
  const [dateRange, setDateRange] = useState(30); // Default to 30 days

  // Add loading states
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [createOrderLoading, setCreateOrderLoading] = useState(false);

  // State for table pagination
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  // Memoize pageSize to prevent unnecessary effect triggers
  const pageSize = useMemo(
    () => tablePagination.pageSize,
    [tablePagination.pageSize]
  );

  // GraphQL query for fetching RemovebgCredits
  const getCreditsQuery = `
    query getCredits($orgId: ID!, $first: Int!) {
      findRemovebgCredits(
        first: $first
        where: {org_pointer: {have: {objectId: {equalTo: $orgId}}}}
      ) {
        count
        edges {
          node {
            freeCreditsAmount
            paidCreditsAmount
          }
        }
      }
    }
  `;

  // GraphQL query for fetching RemovebgOrders with offset-based pagination
  const getRemovebgOrdersQuery = `
    query getRemovebgOrders($orgId: ID!, $first: Int!, $skip: Int) {
      findRemovebgOrders(
        first: $first
        skip: $skip
        where: {org_pointer: {have: {objectId: {equalTo: $orgId}}}}
        order: [createdAt_DESC]
      ) {
        count
        edges {
          node {
            objectId
            createdAt
            transactionDate
            freeCreditsAmount
            paidCreditsAmount
            note
            isPaid
            paymentReference
            transactionStatus
          }
        }
      }
    }
  `;
  
  // Query to fetch all orders for charts (up to 1000)
  const getAllOrdersQuery = `
    query getAllOrders($orgId: ID!, $first: Int!) {
      findRemovebgOrders(
        first: $first
        where: {org_pointer: {have: {objectId: {equalTo: $orgId}}}}
        order: [createdAt_DESC]
      ) {
        edges {
          node {
            objectId
            createdAt
            freeCreditsAmount
            paidCreditsAmount
            note
          }
        }
      }
    }
  `;

  const fetchCredits = useCallback(async () => {
    const env = isProd ? "prod" : "dev";
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

    try {
      setCreditsLoading(true);
      const token = await getTokenSilently();

      // Execute the GraphQL query to fetch credit amounts
      const response = await fetch(graphqlUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: getCreditsQuery,
          variables: {
            orgId,
            first: 10
          },
        }),
      });

      const result = await response.json();

      if (result && result.findRemovebgCredits) {
        if (
          result.findRemovebgCredits.edges &&
          result.findRemovebgCredits.edges.length > 0
        ) {
          const creditData = result.findRemovebgCredits.edges[0].node;
          setCredits({
            freeCreditsAmount: creditData.freeCreditsAmount || 0,
            paidCreditsAmount: creditData.paidCreditsAmount || 0,
          });
        }
      }

      return { success: true };
    } catch (error) {
      console.error("Error fetching credits:", error);
      return { success: false, error };
    } finally {
      setCreditsLoading(false);
    }
  }, [isProd, getTokenSilently, orgId, getCreditsQuery]);

  // Function to fetch RemovebgOrders with offset-based pagination
  const fetchOrders = useCallback(
    async (page = 1, pageSize = 5) => {
      const env = isProd ? "prod" : "dev";
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      try {
        setTableLoading(true);
        
        const token = await getTokenSilently();
        
        // Calculate skip based on page and pageSize
        const skip = (page - 1) * pageSize;

        // Execute the GraphQL query to fetch orders with pagination
        const response = await fetch(graphqlUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            query: getRemovebgOrdersQuery,
            variables: {
              orgId,
              first: pageSize,
              skip: skip
            },
          }),
        });

        const result = await response.json();

        if (result && result.findRemovebgOrders) {
          // Update orders data
          if (result.findRemovebgOrders.edges) {
            const orders = result.findRemovebgOrders.edges.map((edge) => ({
              ...edge.node,
              key: edge.node.objectId, // Add key for table component
            }));
            setCreditOrders(orders);
          }

          // Update pagination with total count
          if (result.findRemovebgOrders.count !== undefined) {
            setTablePagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize,
              total: result.findRemovebgOrders.count,
            }));
          }
        }

        return { success: true };
      } catch (error) {
        console.error("Error fetching orders:", error);
        return { success: false, error };
      } finally {
        setTableLoading(false);
      }
    },
    [isProd, getTokenSilently, orgId, getRemovebgOrdersQuery]
  );

  // Calculate statistics for the selected period
  const calculatePeriodStats = useCallback((orders) => {
    const now = moment();
    const startDate = dateRange >= 365 
      ? now.clone().subtract(dateRange / 30.44, 'months').startOf('day')
      : now.clone().subtract(dateRange, 'days').startOf('day');
    
    let creditsUsed = 0;
    let creditsAddon = 0;
    let creditsSubscription = 0;

    orders.forEach((order) => {
      const orderDate = moment(order.createdAt);
      
      // Only process orders within the selected period
      if (orderDate.isSameOrAfter(startDate) && orderDate.isSameOrBefore(now)) {
        // Calculate used credits (negative values)
        if (order.freeCreditsAmount < 0 || order.paidCreditsAmount < 0) {
          const freeUsage = order.freeCreditsAmount < 0 ? Math.abs(order.freeCreditsAmount) : 0;
          const paidUsage = order.paidCreditsAmount < 0 ? Math.abs(order.paidCreditsAmount) : 0;
          creditsUsed += freeUsage + paidUsage;
        }
        
        // Calculate received credits (positive values)
        if (order.freeCreditsAmount > 0 || order.paidCreditsAmount > 0) {
          const totalPositive = (order.freeCreditsAmount > 0 ? order.freeCreditsAmount : 0) + 
                               (order.paidCreditsAmount > 0 ? order.paidCreditsAmount : 0);
          
          // Check if it's an addon purchase based on the note field
          if (order.note === "Acquisto Crediti IA") {
            creditsAddon += totalPositive;
          } else {
            // Otherwise it's subscription credits
            creditsSubscription += totalPositive;
          }
        }
      }
    });

    setPeriodStats({
      creditsUsed,
      creditsAddon,
      creditsSubscription,
    });
  }, [dateRange]);

  // Function to fetch all orders for charts
  const fetchAllOrdersForCharts = useCallback(async () => {
    const env = isProd ? "prod" : "dev";
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

    try {
      const token = await getTokenSilently();
      
      const response = await fetch(graphqlUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: getAllOrdersQuery,
          variables: {
            orgId,
            first: 1000, // Get up to 1000 orders for charts
          },
        }),
      });

      const result = await response.json();

      if (result && result.findRemovebgOrders && result.findRemovebgOrders.edges) {
        const orders = result.findRemovebgOrders.edges.map((edge) => edge.node);
        setAllOrders(orders);
        calculatePeriodStats(orders);
      }
    } catch (error) {
      console.error("Error fetching all orders:", error);
    }
  }, [isProd, getTokenSilently, orgId, getAllOrdersQuery, calculatePeriodStats]);

  useEffect(() => {
    // Fetch initial data
    fetchCredits();
    // Initial fetch of orders for the first page
    fetchOrders(1, pageSize);
    // Fetch all orders for charts
    fetchAllOrdersForCharts();
  }, [fetchCredits, fetchOrders, pageSize, fetchAllOrdersForCharts]);

  // Recalculate stats when dateRange changes
  useEffect(() => {
    if (allOrders.length > 0) {
      calculatePeriodStats(allOrders);
    }
  }, [dateRange, allOrders, calculatePeriodStats]);

  const createCreditOrder = async (values) => {
    try {
      setCreateOrderLoading(true);
      const token = await getTokenSilently();

      const payload = {
        ...values,
        orgId: orgId,
      };

      const response = await fetch(
        `${config.removebg[isProd ? "prod" : "dev"]}/synthetic-transaction`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json();

      return responseData;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setCreateOrderLoading(false);
    }
  };

  const onResultAcked = () => {
    setCreditiModalVisible(false);
    setCreditiModalResult("");
    
    // Refresh data
    fetchCredits();
    fetchOrders(1, pageSize);
    fetchAllOrdersForCharts();
  };

  const onCreate = (values) => {
    createCreditOrder(values)
      .then((response) => {
        setCreditiModalResult("OK");
        
        // Refresh data after successful creation
        fetchCredits();
        fetchOrders(1, pageSize);
        fetchAllOrdersForCharts();
      })
      .catch((error) => {
        console.error(error);
        setCreditiModalResult("ERROR");
      });
  };

  // Handle table pagination change
  const handleTableChange = (pagination) => {
    const currentPage = pagination.current;
    const pageSize = pagination.pageSize;
    
    // Update pagination state immediately to show the user feedback
    setTablePagination({
      ...pagination,
      current: currentPage,
      pageSize: pageSize,
    });
    
    // Fetch the page directly using offset-based pagination
    fetchOrders(currentPage, pageSize);
  };

  const columns = [
    {
      title: "Data",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (data) => format(new Date(data), "dd/MM/yyyy"),
    },
    {
      title: "Ora",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (data) => format(new Date(data), "HH:mm"),
    },
    {
      title: "Crediti Acquistati",
      dataIndex: "paidCreditsAmount",
      key: "paidCreditsAmount",
    },
    {
      title: "Crediti Gratuiti",
      dataIndex: "freeCreditsAmount",
      key: "freeCreditsAmount",
    },
    { title: "Note", dataIndex: "note", key: "note" },
    {
      title: "Costo",
      dataIndex: "isPaid",
      key: "isPaid",
      render: (isPaid) => {
        return (
          <Tag color={isPaid ? "green" : "blue"}>
            {isPaid ? "PAID" : "FREE"}
          </Tag>
        );
      },
    },
    {
      title: "ID Pagamento",
      dataIndex: "paymentReference",
      key: "paymentReference",
    },
    {
      title: "Stato",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
      render: (transactionStatus) => {
        const colors = {
          approved: "green",
          cancelled: "volcano",
          pending: "orange",
        };

        return (
          <Tag color={colors[transactionStatus]}>
            {transactionStatus.toUpperCase()}
          </Tag>
        );
      },
    },
  ];

  // Prepare chart data based on selected date range
  const prepareChartData = useCallback(() => {
    if (!allOrders || allOrders.length === 0) return null;

    // Determine the number of days to show based on dateRange
    const daysToShow = dateRange;
    const aggregateByMonth = dateRange >= 365; // Aggregate by month for 12+ months view
    
    const dates = [];
    const dateData = {};
    
    if (aggregateByMonth) {
      // Group by month for 12+ months view
      const monthsToShow = Math.ceil(dateRange / 30.44);
      for (let i = monthsToShow - 1; i >= 0; i--) {
        const monthKey = moment().subtract(i, "months").format("YYYY-MM");
        dates.push(monthKey);
        dateData[monthKey] = { usage: 0, subscription: 0, addon: 0 };
      }
    } else {
      // Group by day for 30 and 90 days views
      for (let i = daysToShow - 1; i >= 0; i--) {
        const date = moment().subtract(i, "days").format("YYYY-MM-DD");
        dates.push(date);
        dateData[date] = { usage: 0, subscription: 0, addon: 0 };
      }
    }

    allOrders.forEach((order) => {
      const orderMoment = moment(order.createdAt);
      const orderKey = aggregateByMonth 
        ? orderMoment.format("YYYY-MM")
        : orderMoment.format("YYYY-MM-DD");
      
      if (dateData[orderKey]) {
        // Credits usage (negative values)
        if (order.freeCreditsAmount < 0 || order.paidCreditsAmount < 0) {
          const usage = Math.abs(order.freeCreditsAmount || 0) + Math.abs(order.paidCreditsAmount || 0);
          dateData[orderKey].usage += usage;
        }
        // Credits top-ups (positive values)
        if (order.freeCreditsAmount > 0 || order.paidCreditsAmount > 0) {
          const topup = (order.freeCreditsAmount > 0 ? order.freeCreditsAmount : 0) + 
                        (order.paidCreditsAmount > 0 ? order.paidCreditsAmount : 0);
          
          // Check if it's an addon purchase
          if (order.note === "Acquisto Crediti IA") {
            dateData[orderKey].addon += topup;
          } else {
            // Otherwise it's subscription credits
            dateData[orderKey].subscription += topup;
          }
        }
      }
    });

    return {
      labels: dates.map(date => {
        if (aggregateByMonth) {
          return moment(date, "YYYY-MM").format("MMM YY");
        }
        return moment(date).format("DD/MM");
      }),
      datasets: [
        {
          label: "Crediti Utilizzati",
          data: dates.map(date => dateData[date].usage),
          backgroundColor: "rgba(239, 68, 68, 0.5)",
          borderColor: "rgb(239, 68, 68)",
          borderWidth: 2,
          type: 'bar',
          stack: 'usage',
        },
        {
          label: "Crediti in Abbonamento",
          data: dates.map(date => dateData[date].subscription),
          backgroundColor: "rgba(34, 197, 94, 0.5)",
          borderColor: "rgb(34, 197, 94)",
          borderWidth: 2,
          type: 'bar',
          stack: 'credits',
        },
        {
          label: "Addon Acquistati",
          data: dates.map(date => dateData[date].addon),
          backgroundColor: "rgba(59, 130, 246, 0.5)",
          borderColor: "rgb(59, 130, 246)",
          borderWidth: 2,
          type: 'bar',
          stack: 'credits',
        },
      ],
    };
  }, [allOrders, dateRange]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Data",
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Crediti",
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
  };

  const chartData = useMemo(() => prepareChartData(), [prepareChartData]);
  
  // Get chart title based on selected range
  const getChartTitle = () => {
    switch(dateRange) {
      case 30:
        return "Andamento Crediti - Ultimi 30 giorni";
      case 90:
        return "Andamento Crediti - Ultimi 90 giorni";
      case 365:
        return "Andamento Crediti - Ultimi 12 mesi";
      case 730:
        return "Andamento Crediti - Ultimi 24 mesi";
      case 1095:
        return "Andamento Crediti - Ultimi 36 mesi";
      default:
        return "Andamento Crediti";
    }
  };

  return (
    <div>
      <AntTitle level={4} style={{ marginBottom: 16 }}>Saldo attuale</AntTitle>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card>
            <Spin spinning={creditsLoading}>
              <Statistic
                title="Gratuiti"
                value={credits.freeCreditsAmount}
                precision={0}
                valueStyle={{
                  color: credits.freeCreditsAmount > 0 ? "#3f8600" : "#cf1322",
                }}
                suffix="crediti"
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card>
            <Spin spinning={creditsLoading}>
              <Statistic
                title="Acquistati"
                value={credits.paidCreditsAmount}
                precision={0}
                valueStyle={{
                  color: credits.paidCreditsAmount > 0 ? "#3f8600" : "#cf1322",
                }}
                suffix="crediti"
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8} lg={6} xl={4}>
          <Card style={{ height: "100%" }}>
            <Button
              style={{ marginTop: "15px", width: "100%" }}
              type="primary"
              size="large"
              onClick={() => setCreditiModalVisible(true)}
              loading={createOrderLoading}
            >
              Crea Ordine <PlusOutlined />
            </Button>
            <CreditiModal
              onResultAcked={onResultAcked}
              creditiModalResult={creditiModalResult}
              visible={creditiModalVisible}
              onCreate={onCreate}
              onCancel={() => setCreditiModalVisible(false)}
            />
          </Card>
        </Col>
      </Row>
      
      <Divider />
      
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <AntTitle level={4} style={{ margin: 0 }}>Statistiche Utilizzo</AntTitle>
        </Col>
        <Col>
          <Space>
            <Button
              type={dateRange === 30 ? "primary" : "default"}
              onClick={() => setDateRange(30)}
            >
              Ultimi 30 giorni
            </Button>
            <Button
              type={dateRange === 90 ? "primary" : "default"}
              onClick={() => setDateRange(90)}
            >
              Ultimi 90 giorni
            </Button>
            <Button
              type={dateRange === 365 ? "primary" : "default"}
              onClick={() => setDateRange(365)}
            >
              Ultimi 12 mesi
            </Button>
            <Button
              type={dateRange === 730 ? "primary" : "default"}
              onClick={() => setDateRange(730)}
            >
              Ultimi 24 mesi
            </Button>
            <Button
              type={dateRange === 1095 ? "primary" : "default"}
              onClick={() => setDateRange(1095)}
            >
              Ultimi 36 mesi
            </Button>
          </Space>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Card style={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic
              title="Crediti Utilizzati"
              value={periodStats.creditsUsed}
              suffix="crediti"
              valueStyle={{
                color: periodStats.creditsUsed === 0 ? "#8c8c8c" : "#ef4444",
                fontSize: "24px",
                fontWeight: 600,
              }}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
              Ultimi {dateRange === 365 ? "12 mesi" : dateRange === 730 ? "24 mesi" : dateRange === 1095 ? "36 mesi" : dateRange + " giorni"}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Card style={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic
              title="Crediti Acquistati (Addon)"
              value={periodStats.creditsAddon}
              suffix="crediti"
              valueStyle={{
                color: periodStats.creditsAddon === 0 ? "#8c8c8c" : "#3b82f6",
                fontSize: "24px",
                fontWeight: 600,
              }}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
              Ultimi {dateRange === 365 ? "12 mesi" : dateRange === 730 ? "24 mesi" : dateRange === 1095 ? "36 mesi" : dateRange + " giorni"}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Card style={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic
              title="Crediti in Abbonamento"
              value={periodStats.creditsSubscription}
              suffix="crediti"
              valueStyle={{
                color: periodStats.creditsSubscription === 0 ? "#8c8c8c" : "#22c55e",
                fontSize: "24px",
                fontWeight: 600,
              }}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
              Ultimi {dateRange === 365 ? "12 mesi" : dateRange === 730 ? "24 mesi" : dateRange === 1095 ? "36 mesi" : dateRange + " giorni"}
            </div>
          </Card>
        </Col>
      </Row>
      
      {chartData && (
        <Card title={getChartTitle()} style={{ marginBottom: 24 }}>
          <div style={{ height: 300 }}>
            <Chart type="bar" data={chartData} options={chartOptions} />
          </div>
        </Card>
      )}
      
      <Divider />
      
      <AntTitle level={4}>Storico Ordini</AntTitle>
      <Table
        dataSource={creditOrders}
        columns={columns}
        pagination={tablePagination}
        onChange={handleTableChange}
        loading={tableLoading}
      />
    </div>
  );
};

export default OrgCrediti;
